import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import UsersService from '~/services/UsersService';

import { getPhone, normalisePhonePrefix } from './LuxLoyaltyInfiniteProfileDetails.utils';

export default function BookingAuthorityOtpDialog({
  authority,
  open,
  onClose,
  onVerified,
}: {
  authority: App.LuxLoyaltyInfiniteBookingAuthority | null;
  open: boolean;
  onClose: () => void;
  onVerified: (authorityId: string) => void;
}) {
  const tenant = useSelector((state: App.State) => state.tenant);
  const { enqueueSnackbar } = useSnackbar();
  const [code, setCode] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode('');
      setHasSent(false);
      setErrorMessage(null);
    }
  }, [open]);

  const canSend = Boolean(authority?.phonePrefix && authority?.phone);
  const isValidCode = /^\d{6}$/.test(code);

  const sendCode = async () => {
    if (!authority || !canSend) {
      return;
    }

    setIsSending(true);
    setErrorMessage(null);
    try {
      await UsersService.sendOtpSmsCode(normalisePhonePrefix(authority.phonePrefix), authority.phone, tenant.brand);
      setHasSent(true);
      enqueueSnackbar('Booking authority SMS verification code sent', { variant: 'success' });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send verification code.');
    } finally {
      setIsSending(false);
    }
  };

  const verifyCode = async () => {
    if (!authority || !isValidCode) {
      setErrorMessage('Please enter the 6 digit code.');
      return;
    }

    setIsVerifying(true);
    setErrorMessage(null);
    try {
      await UsersService.validateOtpSmsCode(
        normalisePhonePrefix(authority.phonePrefix),
        authority.phone,
        code,
        tenant.brand,
      );
      onVerified(authority.id);
      enqueueSnackbar('Booking authority phone verified', { variant: 'success' });
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not verify code.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSending || isVerifying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verify booking authority phone</DialogTitle>
      <DialogContent dividers>
        {authority && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {[authority.title, authority.firstName, authority.lastName].filter(Boolean).join(' ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {authority.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getPhone(authority)}
              </Typography>
            </Box>
            <Typography variant="body2">
              Send a 6 digit code to the stored booking authority phone number and ask the caller to read it back.
            </Typography>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                onClick={sendCode}
                disabled={!canSend || isSending || isVerifying}
                sx={{ minWidth: 128, whiteSpace: 'nowrap' }}
              >
                {hasSent ? 'Resend code' : 'Send code'}
              </Button>
              <TextField
                label="Verification code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric' }}
                disabled={isSending || isVerifying}
                fullWidth
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending || isVerifying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={verifyCode} disabled={!isValidCode || isSending || isVerifying}>
          Verify
        </Button>
      </DialogActions>
    </Dialog>
  );
}
